// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type {
  EvolveApiBaseRoute,
  EvolveApiResponse,
  EvolveApiBody,
  PageFetcher,
  AdditionalRequestConfig,
} from '@evolvemep/api-ts';
import type { ColDef, IServerSideGetRowsRequest } from 'ag-grid-enterprise';

import type { EvolveIconName } from 'assets/icons/EvolveIcon';
import type { AgGridEditableProps } from 'helpers/ag-grid/EditableCells/AgGridEditable';

export type DetailTable<
  /** The base API Route of the detail table Paginated GET */
  DetailRoute extends EvolveApiBaseRoute<'GET_PAGINATED'>,
  DetailRowType extends EvolveApiResponse<'GET_PAGINATED', DetailRoute>['data'][number],
  ParentRowType,
  DetailTitle extends string,
  DetailPatchRoute extends EvolveApiBaseRoute<'PATCH'> = EvolveApiBaseRoute<'PATCH'>,
  DetailPatchBody extends EvolveApiBody<'PATCH', DetailPatchRoute> = EvolveApiBody<'PATCH', DetailPatchRoute>,
> = {
  title: DetailTitle;
  icon: EvolveIconName | null;
  exportable?: boolean;
  /**
   * If true, hides the detail table entirely,
   * @default false
   */
  hidden?: boolean;
  /**
   * If `true`, will hide the header on this detail table.
   * @default false
   */
  hideHeader?: boolean;
  autoExpand?: boolean;
  colDef: ColDef<DetailRowType>[] | ((parentData: ParentRowType) => ColDef<DetailRowType>[]);
  fetchPage: PageFetcher<DetailRoute>;
  rightSide?:
    | ReactNode
    | ((parentData: ParentRowType, refreshDetailGrid: (openIfClosed?: boolean) => void) => ReactNode);
  /**
   * This function is used to map additional URL params (or anything else)
   * onto the detail table Paginated GET request.
   *
   * For example, if the parent is a WorkOrder,
   * and the detail table requires a URL parameter `workOrderId`,
   * this function would be
   *
   *     configMapper: (row: workOrder) => ({
   *       params: {
   *         workOrderId: row.workOrderId,
   *       },
   *     })
   * */
  configMapper: (
    parentData: ParentRowType,
    request: IServerSideGetRowsRequest,
  ) => AdditionalRequestConfig<'GET_PAGINATED', DetailRoute>;
  /**
   * Any `context` to pass directly to the detail AgGridReact component.
   */
  context?: unknown;
} & {
  exportable?: true;
  exportFileName?: (parentData: ParentRowType) => string;
} & {
  editing?: Pick<
    AgGridEditableProps<DetailRowType, DetailPatchRoute, DetailPatchBody>,
    'patchCall' | 'requestConverter' | 'configMapper'
  > & {
    editable: boolean;
  };
};

export declare const detailTableSymbol: unique symbol;

export const detailTable = <
  /** The base API Route of the detail table Paginated GET */
  DetailRoute extends EvolveApiBaseRoute<'GET_PAGINATED'>,
  DetailRowType extends EvolveApiResponse<'GET_PAGINATED', DetailRoute>['data'][number],
  ParentRowType,
  DetailTitle extends string,
  DetailPatchRoute extends EvolveApiBaseRoute<'PATCH'> = EvolveApiBaseRoute<'PATCH'>,
  DetailPatchBody extends EvolveApiBody<'PATCH', DetailPatchRoute> = EvolveApiBody<'PATCH', DetailPatchRoute>,
>(
  config: DetailTable<DetailRoute, DetailRowType, ParentRowType, DetailTitle, DetailPatchRoute, DetailPatchBody>,
) =>
  // We cast though `unknown` so that we can brand the output.
  // This prevents someone from passing in a `DetailTable` to `useServerSideGrid`
  // without going through `detailTable()` for type safety.
  config as unknown as DetailTable<
    // If DetailRoute is defined on the output type, TypeScript will create a union of all possible DURLs
    // and none of the DetailTable configs will be compatible with each other.
    // oxlint-disable-next-line typescript/no-explicit-any
    any,
    DetailRowType,
    ParentRowType,
    DetailTitle
  > & {
    [detailTableSymbol]: never;
  };
